import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@composables/handleError.js";
import { useSurveyStore } from "@stores/survey/survey";
import { useAiInsightConversationsStore } from "@stores/survey/assistant/aiInsightConversations";
import type { AiInsightMessage } from "@/types";
import { captureEvent } from "@/lib/analytics";

export const useAiInsightConversationMessagesStore = defineStore("aiInsightConversationMessages", () => {
  // state
  const conversationMessages = ref<AiInsightMessage[]>([]);
  const messagesContainerScrollTop = ref(0);
  const askedConversationIds = new Set<number>();

  // stores
  const { token } = storeToRefs(useSurveyStore());

  // computed
  const lastMessageIndex = computed(() => conversationMessages.value.length - 1);

  // actions
  const createConversationMessage = (userQuestion: string) =>
    conversationMessages.value.push({ id: null, question: userQuestion });

  const sendConversationMessage = async (conversationId: number) => {
    const userQuestion = conversationMessages.value[conversationMessages.value.length - 1].question;
    try {
      const url = `/surveys/${token.value}/ai_insight_conversations/${conversationId}/messages`;
      const { response, data } = await useMyFetch(url).post({ question: userQuestion }).json<AiInsightMessage>();

      if (response.value.ok) {
        captureEvent("ai_insight_message_sent", { conversation_id: conversationId });
        askedConversationIds.add(conversationId);
        data.value.status = 0;
        Object.assign(conversationMessages.value[lastMessageIndex.value], data.value);
        return data.value.id;
      }
    } catch (err) {
      handleError(err);
    }
  };

  const cancelConversationMessage = async (conversationId: number) => {
    try {
      const url = `/surveys/${token.value}/ai_insight_conversations/${conversationId}/messages/cancel`;
      const { response } = await useMyFetch(url).post().json();
      if (response.value.ok) {
        captureEvent("ai_insight_message_cancelled", { conversation_id: conversationId });
        return true;
      }
      return false;
    } catch (err) {
      handleError(err);
    }
  };

  const updateGeneratedMessage = (externalId: string, answer: string, action: string, status: number, toolName?: string) => {
    const { conversations } = storeToRefs(useAiInsightConversationsStore());
    const conversation = conversations.value.find((conversation) => conversation.external_id === externalId);
    if (!conversation) return;

    if (askedConversationIds.has(conversation.id)) {
      if (action === "function_call" && toolName) {
        captureEvent("ai_insight_tool_called", { conversation_id: conversation.id, tool_name: toolName });
      }
      if (action === "done" || action === "error") askedConversationIds.delete(conversation.id);
    }

    const lastIndex = conversation.messages.length - 1;
    const messageToUpdate = conversation.messages[lastIndex];

    if (messageToUpdate) {
      messageToUpdate.answer = answer;
      messageToUpdate.action = action;
      messageToUpdate.status = status;
    }
  };

  const getMessageFeedback = (index: number) => conversationMessages.value[index]?.user_feedback;

  const updateMessageFeedback = async (index: number, feedback: string) => {
    const { selectedConversation } = storeToRefs(useAiInsightConversationsStore());
    const messageToUpdate = conversationMessages.value[index];
    const feedbackValue = { user_feedback: messageToUpdate.user_feedback === feedback ? "no_opinion" : feedback };
    try {
      const url = `/surveys/${token.value}/ai_insight_conversations/${selectedConversation.value.id}/messages/${messageToUpdate.id}`;
      const { response, data } = await useMyFetch(url).put(feedbackValue).json<AiInsightMessage>();

      if (response.value.ok) {
        conversationMessages.value[index] = data.value;
        captureEvent("ai_insight_feedback_given", {
          feedback: feedbackValue.user_feedback,
        });
      }
    } catch (err) {
      handleError(err);
    }
  };

  const handleReceivedMessage = async (msg) => {
    updateGeneratedMessage(msg.thread_id, msg.text, msg.action, msg.status, msg.tool_name);
  };

  const channelHandlers = (cable, surveyToken: string) => {
    return {
      connected: subscribeToChannel(cable, surveyToken),
      rejected: () => console.log("rejected"),
      received: handleReceivedMessage,
    };
  };

  const subscribeToChannel = (cable, surveyToken: string) => () =>
    cable.perform({
      channel: "AnalysisAssistantChannel",
      action: "subscribe",
      data: { survey_token: surveyToken },
    });

  return {
    conversationMessages,
    messagesContainerScrollTop,
    lastMessageIndex,
    createConversationMessage,
    sendConversationMessage,
    cancelConversationMessage,
    getMessageFeedback,
    updateMessageFeedback,
    handleReceivedMessage,
    channelHandlers,
    subscribeToChannel,
  };
});
